/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'

import { EuiFlexGroup, EuiFlexItem, EuiIcon, EuiLink, EuiText } from '@elastic/eui'

import type { AnyCluster } from '@modules/ccm-lib/types'
import { getLinkedServiceUrl } from '@modules/ccm-lib/urls'
import { useDisableCCMClusterService } from '@modules/ccm-user-lib/hooks'
import { FormattedMessage } from '@modules/i18n'
import { useIsOrganizationTrialExpired } from '@modules/profile-lib/hooks'
import { useIsOrganizationOrCCMAdminVisible } from '@modules/permissions-lib/hooks'

// eslint-disable-next-line import/no-restricted-paths
import { lt } from '@/lib/semver'

import ServiceItem from './ServiceItem'
import { SkeletonServiceItem } from './SkeletonServiceItem'
import OptionalDocumentationLink from './OptionalDocumentationLink'

import type { FC, ReactNode } from 'react'
import type { ServiceCardStatusBadge } from './types'

type Props = {
  cluster: AnyCluster
}

const EISServiceItem: FC<Props> = ({ cluster }) => {
  const badge = useEISServiceStatus(cluster)

  const rightSideContent = useGetEISServiceRightSideContent(cluster)

  const { mutate: switchOffEISService } = useDisableCCMClusterService()

  const handleDisableEISService = () => {
    switchOffEISService({
      id: cluster.id,
      serviceName: 'eis',
    })
  }

  if (badge === undefined) {
    return <SkeletonServiceItem />
  }

  return (
    <ServiceItem
      serviceName='Elastic Inference Service'
      badge={badge}
      zoneId={undefined} // TODO: add region id from the API payload
      description={
        <FormattedMessage
          id='ccm.cluster-services.eis.description'
          defaultMessage='Tap into AI-powered search and chat—no ML model deployment or management needed. <learnMoreLink></learnMoreLink>'
          values={{
            learnMoreLink: () => <OptionalDocumentationLink cluster={cluster} serviceName='eis' />,
          }}
        />
      }
      rightSideContent={rightSideContent}
      onDisable={handleDisableEISService}
      showContextMenu={false}
    />
  )
}

const useGetEISServiceRightSideContent = (cluster: AnyCluster): ReactNode => {
  const navigate = useNavigate()

  const isCloudTrialExpired = useIsOrganizationTrialExpired(cluster.metadata.organization_id)

  const isOrganizationAdminVisible = useIsOrganizationOrCCMAdminVisible()

  if (isCloudTrialExpired) {
    return (
      <EuiFlexGroup alignItems='center' gutterSize='s'>
        <EuiFlexItem>
          <EuiText color='subdued' size='s'>
            <em>
              <FormattedMessage
                id='ccm.cluster-services.eis.trial-expired'
                defaultMessage='Requires paying Cloud account'
              />
            </em>
          </EuiText>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiIcon type='info' aria-hidden={true} />
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  if (!isOrganizationAdminVisible) {
    return (
      <EuiFlexGroup alignItems='center' gutterSize='xs'>
        <EuiFlexItem grow={false}>
          <EuiText color='subdued' size='s'>
            <em>
              <FormattedMessage
                id='ccm.cluster-services.eis.permission-denied'
                defaultMessage='Only admins can manage services'
              />
            </em>
          </EuiText>
        </EuiFlexItem>
        {/* TODO: add tooltip with the link to the documentation */}
        {/* TODO: add accessibility label for the icon */}
        <EuiFlexItem grow={false}>
          <EuiIcon type='info' aria-hidden={true} />
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  if (isEISUnsupported(cluster)) {
    return (
      <EuiFlexGroup alignItems='center' gutterSize='s'>
        <EuiFlexItem>
          <EuiText color='subdued' size='s'>
            <em>
              <FormattedMessage
                id='ccm.cluster-services.eis.unsupported'
                defaultMessage='Requires Elasticsearch version 9.3+'
              />
            </em>
          </EuiText>
        </EuiFlexItem>
        {/* TODO: add tooltip with the link to the documentation */}
        {/* TODO: add accessibility label for the icon */}
        <EuiFlexItem grow={false}>
          <EuiIcon type='info' aria-hidden={true} />
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  if (cluster.services.eis.enabled) {
    const eisLink = getLinkedServiceUrl('eis', cluster)

    return (
      eisLink && (
        <EuiLink onClick={() => navigate(eisLink)}>
          <FormattedMessage id='ccm.cluster-services.eis.open-link' defaultMessage='Open' />
        </EuiLink>
      )
    )
  }

  //We know that EIS is not enabled, so we can return go connect in kibana
  return (
    <EuiFlexGroup alignItems='center' gutterSize='s'>
      <EuiFlexItem>
        <EuiText color='subdued' size='s'>
          <em>
            <FormattedMessage
              id='ccm.cluster-services.eis.not-enabled'
              defaultMessage='Navigate to your self-managed Kibana to manage this service'
            />
          </em>
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiIcon type='info' aria-hidden={true} />
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

const useEISServiceStatus = (cluster: AnyCluster): ServiceCardStatusBadge | undefined => {
  const organizationId = cluster.metadata.organization_id

  const isTrialExpired = useIsOrganizationTrialExpired(organizationId)

  if (isTrialExpired) {
    return 'trial_expired'
  }

  return cluster.services.eis.enabled ? 'enabled' : 'not_enabled'
}

const isEISUnsupported = (cluster: AnyCluster): boolean =>
  lt(cluster.self_managed_cluster.version, '9.3.0')

export default EISServiceItem
